import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";

import { format } from "date-fns";
import { Contact, FormValues, ModalState } from "../types";
import {
  useAddFeedback360,
  useCreateFeedback360,
  useGetFeedback360,
  useGetFeedback360Result,
} from "../hooks";
import { createFeedback360ValidationSchema } from "../validation";
import SuccessModal from "./SuccessModal";
import ContactList from "./ContactList";
import Button from "../../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";

const RequestedFeedback: React.FC = () => {
  const { t } = useTranslation("myArea");
  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);
  const [isFirstSubmit, setIsFirstSubmit] = useState(true);

  const { data: existingFeedbackRequests, isLoading: isLoadingExisting } =
    useGetFeedback360();

  const { data: feedbackResult, isLoading } = useGetFeedback360Result();

  const createMutation = useCreateFeedback360();
  const addMutation = useAddFeedback360();

  useEffect(() => {
    if (existingFeedbackRequests && existingFeedbackRequests.length > 0) {
      setIsFirstSubmit(false);
    }
  }, [existingFeedbackRequests]);

  useEffect(() => {
    if (createMutation.isSuccess || addMutation.isSuccess) {
      setModalState(ModalState.SUCCESS);
    }
  }, [createMutation.isSuccess, addMutation.isSuccess]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd.MM.yyyy");
    } catch (e) {
      return dateString;
    }
  };

  const getInitialValues = () => {
    let initialContacts: Contact[] = [];

    // Add existing contacts (if any)
    if (existingFeedbackRequests && existingFeedbackRequests.length > 0) {
      initialContacts = existingFeedbackRequests.map((item, index) => ({
        id: index + 1,
        email: item.email,
        type: item.type,
        disabled: true,
        requestDate: formatDate(item.requestDate),
      }));
    }

    // Add empty fields for new contacts (ensure at least 8 total)
    const additionalFieldsNeeded = Math.max(8 - initialContacts.length, 0);
    if (additionalFieldsNeeded > 0) {
      const startId = initialContacts.length + 1;
      const emptyContacts = Array.from(
        { length: additionalFieldsNeeded },
        (_, index) => ({
          id: startId + index,
          email: "",
          type: "",
        })
      );

      initialContacts = [...initialContacts, ...emptyContacts];
    }

    return {
      contacts: initialContacts,
      comment: "",
    };
  };

  // Pass isFirstSubmit to the validation schema
  const validationSchema = createFeedback360ValidationSchema(t, isFirstSubmit);

  const handleSubmit = (values: FormValues) => {
    if (!values) return;

    // Filter out only valid new contacts (not disabled)
    const validNewContacts = values.contacts.filter(
      (contact) => !contact.disabled && contact.email && contact.type
    );

    // For first submit, we need at least 8 valid contacts
    // For add, we need at least 1 new valid contact
    if (isFirstSubmit && validNewContacts.length < 8) {
      return;
    } else if (!isFirstSubmit && validNewContacts.length < 1) {
      return;
    }

    // Use the appropriate mutation based on whether this is the first submission
    if (isFirstSubmit) {
      createMutation.mutate(values);
    } else {
      addMutation.mutate(values);
    }
  };

  const renderModalContent = () => {
    switch (modalState) {
      case ModalState.SUCCESS:
        return (
          <SuccessModal onClose={() => setModalState(ModalState.CLOSED)} />
        );
      default:
        return null;
    }
  };

  if (isLoadingExisting) {
    return (
      <div className="w-full text-center py-8">
        {t("assessments.feedback360.loading")}
      </div>
    );
  }

  return (
    <div className="w-full mx-auto py-6">
      {renderModalContent()}

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, errors, touched, isValid, dirty }) => (
          <Form className="">
            <ContactList values={values} />
            {(!feedbackResult || feedbackResult === null) && (
              <>
                {/* Comment Section */}
                <div className="grid grid-cols-7">
                  <div className="md:col-span-6 col-span-7 relative mb-1">
                    <div className="mb-1">
                      {t("assessments.feedback360.comment-description")}
                    </div>
                    <Field
                      as="textarea"
                      name="comment"
                      rows={4}
                      placeholder={t(
                        "assessments.feedback360.comment-placeholder"
                      )}
                      className="w-full px-4 border border-gray-300 rounded-lg"
                    />

                    {/* Submit Button */}
                    <div>
                      <Button
                        type="submit"
                        className="mt-4 w-full"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.SMALL}
                        disabled={
                          !(isValid && dirty) ||
                          createMutation.isLoading ||
                          addMutation.isLoading
                        }
                      >
                        {createMutation.isLoading || addMutation.isLoading
                          ? t("assessments.feedback360.submitting")
                          : t("assessments.feedback360.submit-button")}
                      </Button>
                    </div>
                    {/* Display validation errors directly from the schema */}
                    {dirty &&
                      errors.contacts &&
                      typeof errors.contacts === "string" && (
                        <div className="text-red-500 text-sm mt-1">
                          {errors.contacts}
                        </div>
                      )}

                    {/* Display API errors */}
                    {(createMutation.isError || addMutation.isError) && (
                      <div className="text-red-500 text-sm mt-1">
                        {t("assessments.feedback360.error")}:{" "}
                        {(createMutation.error as any)?.message ===
                          "FEEDBACK_360_LIMIT_EXCEEDED" ||
                        (addMutation.error as any)?.message ===
                          "FEEDBACK_360_LIMIT_EXCEEDED"
                          ? t("assessments.feedback360.limit_exceeded_error")
                          : (createMutation.error as any)?.message ||
                            (addMutation.error as any)?.message ||
                            t("assessments.feedback360.unknown_error")}
                      </div>
                    )}
                  </div>{" "}
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RequestedFeedback;
